import { Store } from "lucide-react";
import { observer } from "mobx-react-lite";
import type { FC } from "react";

import { GreenSwitch } from "@/components/atoms";
import { cn } from "@/lib/utils";

import type { DishDrawerStore } from "../dishDrawerStore";

interface Props {
  store: DishDrawerStore;
  disabled?: boolean;
}

const StepAvailability: FC<Props> = observer(({ store, disabled = false }) => {
  const { branchAvailability, allBranchesAvailable, dish } = store;

  if (branchAvailability.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <h2 className="text-xl font-bold">Branch availability</h2>
        <div className="border rounded-lg p-8 text-center text-gray-500 text-sm">
          <Store className="w-8 h-8 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-gray-700 mb-1">No branches found</p>
          <p>
            Please add at least one branch before saving a dish. Go to your
            brand settings to add branches.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Branch availability</h2>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>All branches</span>
          <GreenSwitch
            checked={allBranchesAvailable}
            onCheckedChange={v => store.toggleAllBranches(v)}
            data-testid="dish-drawer-all-branches-toggle"
            disabled={disabled}
          />
        </div>
      </div>

      {!dish.active && (
        <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          The master &quot;Available&quot; toggle is off — this dish is hidden
          everywhere on portal regardless of per-branch settings.
        </p>
      )}

      <div className="flex flex-col gap-3">
        {branchAvailability.map(branch => (
          <div
            key={branch.branchId}
            className={cn(
              "flex items-center gap-4 p-4 border rounded-lg",
              branch.available ? "bg-white" : "bg-gray-100"
            )}
          >
            <div
              className={cn(
                "w-10 h-10 rounded-lg flex items-center justify-center shrink-0",
                branch.available ? "bg-[#fff9d5]" : "bg-gray-200"
              )}
            >
              <Store className="w-5 h-5 text-gray-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">
                {branch.branchName}
              </p>
              {branch.city && (
                <p className="text-xs text-gray-500">{branch.city}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm text-gray-500">
                {branch.available ? "Available" : "Hidden"}
              </span>
              <GreenSwitch
                checked={branch.available}
                onCheckedChange={v =>
                  store.toggleBranchAvailability(branch.branchId, v)
                }
                disabled={disabled}
                data-testid={`dish-drawer-branch-toggle-${branch.branchId}`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export default StepAvailability;
